import { Typography, Box, useMediaQuery } from "@mui/material";
import { useTheme } from '@mui/material/styles';

const ForLoop = () => {
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flex: 1,
            overflowY: 'auto',
            padding: 24,
        }}>
            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>For Loop</strong>
                <br /><br />
                <p>In this example, we'll demonstrate how to use a <strong>For Loop</strong> with API calls in a flow.</p>
                <p>We'll work with two APIs:</p>
                <ol>
                    <li><strong>Users API</strong> – Returns an array of user objects.</li>
                    <li><strong>Posts API</strong> – Fetches posts for a user using their <code>userId</code>.</li>
                </ol>
            </Typography>

            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>Users API</strong>
                <br /><br />
                <p>This API returns an array of user objects as shown in the screenshot below.</p>
            </Typography>
            <img src="/flows/forLoop/forLoopUsersAPI.png" alt="Users API response" style={imageStyle(isMediumScreen)} />

            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>Posts API</strong>
                <br /><br />
                <p>
                    This API requires <code>userId</code> as a path parameter and returns all posts made by that user.
                    The URL format uses a variable: <br />
                    <code>https://jsonplaceholder.typicode.com/users/{`{{userId}}`}/posts</code>
                </p>
            </Typography>
            <img src="/flows/forLoop/forLoopPostsAPI.png" alt="Posts API response" style={imageStyle(isMediumScreen)} />

            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>Flow Configuration</strong>
                <br /><br />
                <p>
                    Start by linking the <strong>Users API</strong> to the Start Node. Then add a <strong>For Loop</strong> node after the Users API, followed by the <strong>Posts API</strong> node inside the loop.
                </p>
            </Typography>
            <img src="/flows/forLoop/forLoopBoard.png" alt="Flow setup" style={imageStyle(isMediumScreen)} />

            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>Flow Variables</strong>
                <br /><br />
                <p>
                    Both the Request and For Loop nodes support <strong>Flow Variables</strong>, allowing data to be shared across the flow at runtime via the flow store.
                </p>
            </Typography>

            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>Users API – Setting Flow Variables</strong>
                <br /><br />
                <p>
                    In the Users API node, configure the post-response flow variable as:
                    <br />
                    <code>userArray: response.body</code><br />
                    This makes the full array of users accessible to subsequent nodes in the flow.
                </p>
            </Typography>
            <img src="/flows/forLoop/forLoopUsersPostResponseVariables.png" alt="Users API flow variable" style={imageStyle(isMediumScreen)} />

            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>For Loop – Flow Variables</strong>
                <br /><br />
                <p>
                    In the For Loop node, set the input variable to <code>{`{{userArray}}`}</code>. The loop will iterate over each item in this array.
                    <br />
                    Inside the loop, you can define dynamic variables such as <code>index</code> and <code>item</code>. For example, set:
                    <br />
                    <code>userId: item.id</code><br />
                    This <code>userId</code> will be used in the Posts API call.
                </p>
            </Typography>
            <img src="/flows/forLoop/forLoopEachIterationVariables.png" alt="For Loop flow variables" style={imageStyle(isMediumScreen)} />

            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>Using Variables in Posts API</strong>
                <br /><br />
                <p>
                    The Posts API requires a <code>userId</code>. Under the Flow Variables section of the Posts API node,
                    set a pre-request variable by choosing the <strong>“Pick From FlowStore”</strong> option to use the
                    <code> userId</code> value set in the loop.
                </p>
            </Typography>
            <img src="/flows/forLoop/forLoopPostsApiPreVariables.png" alt="Posts API pre variables" style={imageStyle(isMediumScreen)} />

            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>Execution Result</strong>
                <br /><br />
                <p>
                    When the flow executes:
                    <ul>
                        <li>The Users API is called once to get all users.</li>
                        <li>The Posts API is then called once per user (i.e., N times for N users).</li>
                    </ul>
                    The execution flow is shown below.
                </p>
            </Typography>
            <img src="/flows/forLoop/forLoopExecution.png" alt="Flow execution result" style={imageStyle(isMediumScreen)} />
        </Box>
    );
};

const imageStyle = (isMediumScreen) => ({
    width: isMediumScreen ? '100%' : '80%',
    height: 'auto',
    display: 'block',
    objectFit: 'contain',
    marginTop: '16px',
});

export default ForLoop;
